import { X } from 'lucide-react';
import './Banners.css';

export function InstallBanner({
  onInstall,
  onDismiss,
}: {
  onInstall: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="install-banner">
      <div className="install-banner-text">Установить приложение</div>
      <div className="install-banner-actions">
        <button className="install-banner-button" type="button" onClick={onInstall}>
          Установить
        </button>
        <button
          className="icon-button install-banner-close"
          type="button"
          onClick={onDismiss}
          aria-label="Скрыть"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * Two shapes in one banner, because they answer the same question. On iOS in a
 * browser tab there is nothing to tap — push only reaches a home-screen install,
 * so asking for permission there would fail — and the guidance replaces the CTA.
 */
export function NotificationsBanner({
  needsHomeScreen,
  message,
  busy,
  onEnable,
  onDismiss,
}: {
  needsHomeScreen: boolean;
  message: string | null;
  busy: boolean;
  onEnable: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="install-banner notifications-banner">
      <div className="install-banner-text">
        {needsHomeScreen ? (
          <>
            Чтобы получать уведомления, добавьте приложение на экран «Домой»: «Поделиться» →
            «На экран „Домой“».
          </>
        ) : (
          (message ?? 'Включить уведомления о новых сообщениях?')
        )}
      </div>
      <div className="install-banner-actions">
        {!needsHomeScreen && (
          <button
            className="install-banner-button"
            type="button"
            onClick={onEnable}
            disabled={busy}
          >
            {busy ? 'Подождите…' : 'Включить'}
          </button>
        )}
        <button
          className="icon-button install-banner-close"
          type="button"
          onClick={onDismiss}
          aria-label="Скрыть"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * The newer APK, inside the Android app — see client/src/lib/useAndroidUpdate.ts
 * for why this exists at all and nowhere else.
 *
 * One button through the whole thing rather than «Скачать» and then
 * «Установить»: from where somebody is sitting there is a single action, and
 * the two moments where it genuinely stops — a download that failed, a phone
 * that has not allowed installs — are the two where the label changes and a
 * sentence appears under it.
 */
export function UpdateBanner({
  versionName,
  stage,
  progress,
  message,
  onAct,
  onDismiss,
}: {
  versionName: string;
  stage: 'offer' | 'downloading' | 'ready' | 'failed';
  progress: number | null;
  message: string | null;
  onAct: () => void;
  onDismiss: () => void;
}) {
  const label =
    stage === 'downloading'
      ? // A percentage only when the server sent a length; «Загрузка…» rather
        // than a bar stuck at zero when it did not.
        progress === null
        ? 'Загрузка…'
        : `Загрузка ${Math.round(progress * 100)}%`
      : stage === 'ready'
        ? 'Установить'
        : stage === 'failed'
          ? 'Повторить'
          : 'Обновить';

  return (
    <div className="install-banner update-banner">
      <div className="install-banner-text">
        <div>Новая версия {versionName}</div>
        {/* Only the sentence for a stop — a failed download, installs not
            allowed. Release notes used to sit here too and were removed: the
            manifest still carries them for the download page, but the banner's
            whole job is the one action. */}
        {message !== null && <div className="update-banner-note">{message}</div>}
      </div>
      <div className="install-banner-actions">
        <button
          className="install-banner-button"
          type="button"
          onClick={onAct}
          disabled={stage === 'downloading'}
        >
          {label}
        </button>
        <button
          className="icon-button install-banner-close"
          type="button"
          onClick={onDismiss}
          aria-label="Скрыть"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/** No dismiss control by design: it goes away when the connection comes back. */
export function OfflineBar() {
  return (
    <div className="offline-bar" role="status">
      Нет соединения
    </div>
  );
}
